/**
 * What a Rolling verify costs, and how often a monthly ceiling lets it read a
 * vault back (ADR 0028; **Rolling verify** in the desktop glossary). A Rolling
 * verify reads about the vault's stored size once per cycle, and the person
 * sees what that costs before it runs. Read by the desktop's Scheduled check
 * setting (#1105) and the summary email and portal (#1114), so no two screens
 * quote different numbers for the same vault.
 *
 * Amazon S3, Google Cloud Storage and Azure are priced from
 * `hyperscalerPricing.ts`: egress plus the class's own `retrievalPerGb`.
 * Backblaze B2 and Wasabi bill by egress rules, not a flat rate, and those
 * rules are the constants below. No provider's free monthly allowance (the
 * hyperscalers' 100 GB) is ever subtracted: other use of the account shares
 * it, and the app can't see how much of it is left.
 */

import { formatDollars, lookupStorageTierPrice } from "./hyperscalerPricing";
import { UNRECOGNISED_PROVIDER_CLASS, lookupStorageClass } from "./storageClasses";
import type { VaultStorageTier } from "./storageTier";
import type { DestinationKind } from "./types";

// -- Egress rules -----------------------------------------------------------

/**
 * Backblaze B2: a month's egress is free up to this many times the average
 * data stored that month. `docs/storage-classes.md`, Backblaze B2, **Egress**
 * [B2-P].
 */
export const BACKBLAZE_FREE_EGRESS_PER_STORED = 3;

/**
 * Dollars per gigabyte of Backblaze B2 egress past
 * `BACKBLAZE_FREE_EGRESS_PER_STORED`. `docs/storage-classes.md`, Backblaze
 * B2, **Egress** [B2-P], checked October 2026.
 */
export const BACKBLAZE_EGRESS_PER_GB = 0.01;

/**
 * Wasabi: a month's egress is free while it stays at or below this many times
 * the active storage volume. Past it Wasabi bills nothing per GB but may
 * limit or suspend the account, so there is no price for reading a Wasabi
 * vault back faster. `docs/storage-classes.md`, Wasabi, **Egress** [WA-F],
 * checked October 2026.
 */
export const WASABI_FREE_EGRESS_PER_STORED = 1;

// ---------------------------------------------------------------------------

/** Bytes to the terabyte, decimal, as the price tables count them. */
const BYTES_PER_TB = 1e12;

/** Bytes to the gigabyte, decimal, as the price tables count them. */
const BYTES_PER_GB = 1e9;

/** Gigabytes to the terabyte, as the price tables count them. */
const GB_PER_TB = 1000;

/**
 * Days to the month, as the restore estimate counts them. A 30-day cycle
 * therefore reads the stored size exactly once a month, which is what keeps
 * Wasabi's 30-day cycle inside its policy and B2's 10-day one inside its 3×.
 */
const DAYS_PER_MONTH = 30;

/**
 * What a Rolling verify is priced against: a vault's storage tier, or, for a
 * vault with none (a folder, a NAS, a sign-in cloud), its destination kind.
 */
export type RollingVerifyTier = VaultStorageTier | Exclude<DestinationKind, "s3">;

/** One storage type's or storage tier's default cycle; `null` is Rolling verify off. */
export interface DefaultRollingVerifyCycle {
  readonly tier: RollingVerifyTier;
  readonly cycleDays: number | null;
}

/** Thirty days where reading back is free: about the stored size read each month. */
const FREE_CYCLE_DAYS = 30;

/**
 * Off where reading back is billed (Amazon S3, Google Cloud Storage, Azure, an
 * unrecognised S3-compatible provider), so a default never starts a bill, and
 * on an Archive vault's tier, where Rolling verify is never offered.
 */
const OFF = null;

/**
 * The cycle a vault this PC creates starts its Rolling verify at, one row per
 * storage type and storage tier (ADR 0028). Free where it is on, which
 * `rollingVerifyMonthlyCost` confirms in the tests. Wasabi's 90 days keeps
 * reading back well inside its free-egress policy, leaving the rest of the
 * stored volume for restores and other use.
 */
export const DEFAULT_ROLLING_VERIFY_CYCLES: readonly DefaultRollingVerifyCycle[] = [
  // -- No storage tier ------------------------------------------------------
  { tier: "local", cycleDays: FREE_CYCLE_DAYS },
  { tier: "network", cycleDays: FREE_CYCLE_DAYS },
  { tier: "google-drive", cycleDays: FREE_CYCLE_DAYS },
  { tier: "onedrive", cycleDays: FREE_CYCLE_DAYS },
  { tier: "dropbox", cycleDays: FREE_CYCLE_DAYS },
  // -- Amazon S3 ------------------------------------------------------------
  { tier: { provider: "aws", storageClass: "STANDARD" }, cycleDays: OFF },
  { tier: { provider: "aws", storageClass: "STANDARD_IA" }, cycleDays: OFF },
  { tier: { provider: "aws", storageClass: "ONEZONE_IA" }, cycleDays: OFF },
  { tier: { provider: "aws", storageClass: "GLACIER_IR" }, cycleDays: OFF },
  { tier: { provider: "aws", storageClass: "GLACIER" }, cycleDays: OFF },
  { tier: { provider: "aws", storageClass: "DEEP_ARCHIVE" }, cycleDays: OFF },
  // -- Google Cloud Storage -------------------------------------------------
  { tier: { provider: "gcs", storageClass: "STANDARD" }, cycleDays: OFF },
  { tier: { provider: "gcs", storageClass: "NEARLINE" }, cycleDays: OFF },
  { tier: { provider: "gcs", storageClass: "COLDLINE" }, cycleDays: OFF },
  { tier: { provider: "gcs", storageClass: "ARCHIVE" }, cycleDays: OFF },
  // -- Azure Blob Storage ---------------------------------------------------
  { tier: { provider: "azure", storageClass: "Hot" }, cycleDays: OFF },
  { tier: { provider: "azure", storageClass: "Cool" }, cycleDays: OFF },
  { tier: { provider: "azure", storageClass: "Cold" }, cycleDays: OFF },
  { tier: { provider: "azure", storageClass: "Archive" }, cycleDays: OFF },
  // -- Backblaze B2: free up to 3× stored, so 30 days is well inside --------
  { tier: { provider: "backblaze", storageClass: null }, cycleDays: FREE_CYCLE_DAYS },
  // -- Wasabi: free while egress stays within the stored volume -------------
  { tier: { provider: "wasabi", storageClass: "STANDARD" }, cycleDays: 90 },
  // -- An S3-compatible provider the app doesn't know -----------------------
  { tier: { provider: "other", storageClass: UNRECOGNISED_PROVIDER_CLASS }, cycleDays: OFF },
];

/**
 * The cycle a vault this PC creates on `tier` starts its Rolling verify at,
 * or `null` for Rolling verify off. A tier with no row, a class the
 * catalogue doesn't list, is off too.
 */
export function defaultRollingVerifyCycleDays(tier: RollingVerifyTier): number | null {
  const row = DEFAULT_ROLLING_VERIFY_CYCLES.find(({ tier: t }) =>
    typeof t === "string" || typeof tier === "string"
      ? t === tier
      : t.provider === tier.provider && t.storageClass === tier.storageClass,
  );
  return row?.cycleDays ?? OFF;
}

/** What a Rolling verify of a vault costs a month at one cycle. */
export type RollingVerifyCost =
  /** Dollars a month; 0 where reading back is free at that cycle. */
  | { kind: "priced"; dollarsPerMonth: number }
  /** An S3-compatible provider, or a class, the app has no prices for. */
  | { kind: "no-price" }
  /** Wasabi, read back faster than its free-egress policy allows; it has no price past it. */
  | { kind: "past-free-egress" }
  /** An Archive vault's storage tier, where Rolling verify is never offered. */
  | { kind: "not-offered" };

/**
 * The monthly cost of reading `storedBytes` back once every `cycleDays`,
 * priced as egress plus the class's own retrieval fee per GB, with no free
 * monthly allowance taken off.
 */
export function rollingVerifyMonthlyCost(tier: RollingVerifyTier, storedBytes: number, cycleDays: number): RollingVerifyCost {
  if (typeof tier === "string") return { kind: "priced", dollarsPerMonth: 0 };

  const storedTb = storedBytes / BYTES_PER_TB;
  const readsPerMonth = DAYS_PER_MONTH / cycleDays;

  switch (tier.provider) {
    case "other":
      return { kind: "no-price" };
    case "backblaze": {
      const billedTb = storedTb * Math.max(0, readsPerMonth - BACKBLAZE_FREE_EGRESS_PER_STORED);
      return { kind: "priced", dollarsPerMonth: billedTb * GB_PER_TB * BACKBLAZE_EGRESS_PER_GB };
    }
    case "wasabi":
      return readsPerMonth <= WASABI_FREE_EGRESS_PER_STORED
        ? { kind: "priced", dollarsPerMonth: 0 }
        : { kind: "past-free-egress" };
    case "aws":
    case "gcs":
    case "azure": {
      const found = lookupStorageClass(tier.provider, tier.storageClass);
      if (found.kind === "class" && found.storageClass.retrieval === "delayed") return { kind: "not-offered" };
      const price = lookupStorageTierPrice(tier.provider, tier.storageClass);
      if (price === null) return { kind: "no-price" };
      const dollarsPerTb = price.egressPerTb + price.retrievalPerGb * GB_PER_TB;
      return { kind: "priced", dollarsPerMonth: storedTb * readsPerMonth * dollarsPerTb };
    }
  }
}

/**
 * A monthly ceiling on a Rolling verify: dollars where the app has prices, or
 * gigabytes read back for an S3-compatible provider it has none for.
 */
export type RollingVerifyCeiling = { dollars: number } | { gb: number };

/** How often a ceiling lets a Rolling verify read the whole vault back. */
export type RollingVerifyCycle =
  /** The shortest whole-day cycle whose month stays within the ceiling. */
  | { kind: "cycle"; days: number }
  /** The ceiling can't cover any read. */
  | { kind: "never" }
  /** A dollar ceiling on a provider, or a class, the app has no prices for: set one in GB. */
  | { kind: "no-price" }
  /** An Archive vault's storage tier, where Rolling verify is never offered. */
  | { kind: "not-offered" };

/**
 * The shortest whole-day cycle a monthly ceiling allows for reading
 * `storedBytes` back, priced by `rollingVerifyMonthlyCost` so the two never
 * disagree: at the cycle returned, that month's cost is within the ceiling,
 * and a day shorter it is not.
 */
export function rollingVerifyCycleForCeiling(
  tier: RollingVerifyTier,
  storedBytes: number,
  ceiling: RollingVerifyCeiling,
): RollingVerifyCycle {
  // Whether the tier is offered or priced at all doesn't depend on the cycle.
  const atAnyCycle = rollingVerifyMonthlyCost(tier, storedBytes, 1).kind;
  if (atAnyCycle === "not-offered") return { kind: "not-offered" };
  if ("gb" in ceiling) {
    const storedGb = storedBytes / BYTES_PER_GB;
    return shortestCycleThat((days) => storedGb * (DAYS_PER_MONTH / days) <= ceiling.gb);
  }
  if (atAnyCycle === "no-price") return { kind: "no-price" };
  const fits = (days: number): boolean => {
    const cost = rollingVerifyMonthlyCost(tier, storedBytes, days);
    return cost.kind === "priced" && cost.dollarsPerMonth <= ceiling.dollars;
  };
  return shortestCycleThat(fits);
}

/**
 * The fewest whole days for which `fits` holds, given that a longer cycle
 * never fits worse; `never` when not even the longest representable one does.
 */
function shortestCycleThat(fits: (days: number) => boolean): RollingVerifyCycle {
  if (fits(1)) return { kind: "cycle", days: 1 };
  let tooShort = 1;
  let long = 2;
  while (!fits(long)) {
    if (long >= Number.MAX_SAFE_INTEGER) return { kind: "never" };
    tooShort = long;
    long = Math.min(long * 2, Number.MAX_SAFE_INTEGER);
  }
  while (long - tooShort > 1) {
    const mid = tooShort + Math.floor((long - tooShort) / 2);
    if (fits(mid)) long = mid;
    else tooShort = mid;
  }
  return { kind: "cycle", days: long };
}

/** Days to the year, for putting a long cycle in years. */
const DAYS_PER_YEAR = 365;

/**
 * A cycle as a person reads it: days under two months, months under two
 * years, then years. Months and years are rounded up, so a cycle is never
 * said to come round sooner than it does.
 */
function cycleInWords(days: number): string {
  if (days <= 1) return "every day";
  if (days < 2 * DAYS_PER_MONTH) return `every ${days} days`;
  if (days < 24 * DAYS_PER_MONTH) return `every ${Math.ceil(days / DAYS_PER_MONTH)} months`;
  return `every ${Math.ceil(days / DAYS_PER_YEAR)} years`;
}

/**
 * The Scheduled check setting's sentence for a ceiling and the cycle it buys
 * (`rollingVerifyCycleForCeiling`): "At $5 a month, every pack is read back
 * every 5 months."
 */
export function rollingVerifyCeilingSentence(
  ceiling: RollingVerifyCeiling,
  cycle: Extract<RollingVerifyCycle, { kind: "cycle" | "never" }>,
): string {
  const amount = "gb" in ceiling ? `${ceiling.gb.toLocaleString("en-US")} GB` : formatDollars(ceiling.dollars);
  const read = cycle.kind === "never" ? "no pack is read back" : `every pack is read back ${cycleInWords(cycle.days)}`;
  return `At ${amount} a month, ${read}.`;
}
